import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Moon, Loader2, PenLine, Download, Share2, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, useAuth, withRefresh } from "@/lib/auth";
import { IntentionKeeper } from "./MoonRitual";

const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });

const MoonSwitch = () => {
  const [on, setOn] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    withRefresh(() => api.get("/moon-reminders"))
      .then(({ data }) => alive && setOn(Boolean(data?.on)))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  const flip = async () => {
    setBusy(true);
    try {
      const { data } = await withRefresh(() => api.put("/moon-reminders", { on: !on }));
      setOn(Boolean(data?.on));
      toast.success(data?.on ? "You’ll receive a gentle email on new and full moon nights." : "Moon reminders are off.");
    } catch {
      toast.error("That didn’t save. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  if (on === null) return null;
  return (
    <div className="mt-8 flex items-start justify-between gap-5 border-t border-[#D9B47A]/15 pt-6">
      <div>
        <p className="font-display text-xl">Moon reminders</p>
        <p className="mt-1 text-sm text-[#A8998C]">A gentle email on new moon nights to plant your intention, and on full moon nights to return to it.</p>
      </div>
      <button data-testid="moon-reminders-switch" role="switch" aria-checked={on} disabled={busy} onClick={flip} className={`relative shrink-0 w-14 h-8 rounded-full border transition-colors duration-300 disabled:opacity-60 ${on ? "bg-[#C2414F] border-[#C2414F]" : "bg-transparent border-[#D9B47A]/40"}`}>
        <span className={`absolute top-1 left-1 w-[22px] h-[22px] rounded-full flex items-center justify-center transition-transform duration-300 ${on ? "translate-x-6 bg-[#F2E8DF]" : "bg-[#D9B47A]/60"}`}>
          {busy && <Loader2 className="w-3 h-3 animate-spin text-[#0A0706]" />}
        </span>
      </button>
    </div>
  );
};

const left = (iso, now) => {
  const ms = Math.max(0, new Date(iso) - now);
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return { ms, d, h, m };
};

const MoonTile = ({ kind, iso, now, next }) => {
  const t = left(iso, now);
  const when = new Date(iso).toLocaleString(undefined, { weekday: "long", day: "numeric", month: "long", hour: "numeric", minute: "2-digit" });
  return (
    <div data-testid={`moon-cal-${kind}`} className={`relative p-5 border transition-colors duration-500 ${next ? "border-[#D9B47A]/50 bg-[#120C0A]" : "border-[#D9B47A]/15"}`}>
      <div className="flex items-center gap-3">
        {kind === "full" ? <span className="w-6 h-6 rounded-full bg-[#F2E8DF] shadow-[0_0_20px_rgba(242,232,223,0.45)]" /> : <span className="w-6 h-6 rounded-full bg-[#0A0706] border border-[#D9B47A]/50 shadow-[inset_-4px_-3px_8px_rgba(217,180,122,0.25)]" />}
        <p className="font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#D9B47A]">Next {kind} moon</p>
      </div>
      <p data-testid={`moon-cal-${kind}-date`} className="mt-3 font-display text-xl leading-snug">{when}</p>
      <p data-testid={`moon-cal-${kind}-countdown`} className="mt-2 font-tt-mono text-[12px] tracking-[0.15em] text-[#CFC2B6]">
        {t.ms < 86400000 ? <span className="text-[#E8838E]">Tonight · in {t.h}h {t.m}m</span> : `in ${t.d}d ${t.h}h ${t.m}m`}
      </p>
    </div>
  );
};

export const MoonCalendar = () => {
  const [cal, setCal] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const load = useCallback(() => {
    withRefresh(() => api.get("/moon-calendar"))
      .then(({ data }) => data?.new_moon && data?.full_moon && setCal(data))
      .catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, [load]);
  const passed = Boolean(cal && (new Date(cal.new_moon) <= now || new Date(cal.full_moon) <= now));
  useEffect(() => {
    if (passed) load();
  }, [passed, load]);
  if (!cal) return null;
  const nextKind = new Date(cal.new_moon) < new Date(cal.full_moon) ? "new" : "full";
  return (
    <div data-testid="moon-calendar" className="mt-6">
      <p className="font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#A8998C]">The sky ahead · the {nextKind} moon comes first</p>
      <div className="mt-3 grid sm:grid-cols-2 gap-3">
        {["new", "full"].map((k) => <MoonTile key={k} kind={k} iso={cal[`${k}_moon`]} now={now} next={k === nextKind} />)}
      </div>
    </div>
  );
};

const MoonNote = ({ e, i, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(e.note ?? "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await withRefresh(() => api.put("/intentions/note", { cycle: e.cycle, text }));
      onSaved(e.cycle, data?.note ?? text.trim());
      setOpen(false);
      toast.success(text.trim() ? "Your moon note is kept beside your intention." : "Moon note removed.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "That didn’t save. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  if (!e.bloomed) return null;
  if (!open) {
    return e.note ? (
      <div data-testid={`moon-note-${i}`} className="mt-4 border-l-2 border-[#F2E8DF]/40 pl-4">
        <p className="font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#D9B47A]">At the full moon, you noticed</p>
        <p data-testid={`moon-note-${i}-text`} className="mt-2 text-[15px] leading-relaxed text-[#DCCFC3] whitespace-pre-line break-words">{e.note}</p>
        <button data-testid={`moon-note-${i}-edit`} onClick={() => { setText(e.note); setOpen(true); }} className="mt-2 text-[13px] text-[#A8998C] underline hover:text-[#D9B47A] transition-colors">Edit note</button>
      </div>
    ) : (
      <button data-testid={`moon-note-${i}-add`} onClick={() => setOpen(true)} className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#D9B47A]/40 px-4 py-2 font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#D9B47A] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors">
        <PenLine className="w-3.5 h-3.5" /> Add a moon note
      </button>
    );
  }
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-4">
      <textarea data-testid={`moon-note-${i}-input`} rows={3} maxLength={500} value={text} onChange={(ev) => setText(ev.target.value)} placeholder="How did this intention grow? What did the full moon show you?" className="w-full bg-transparent border border-[#D9B47A]/25 p-3 text-[15px] placeholder:text-[#8F8176] focus:outline-none focus:border-[#D9B47A] transition-colors resize-none" />
      <div className="mt-2 flex items-center justify-between gap-3">
        <span className="text-[12px] text-[#A8998C]">{text.length}/500 · Only you will see this.</span>
        <div className="flex gap-2">
          <button data-testid={`moon-note-${i}-cancel`} onClick={() => { setText(e.note ?? ""); setOpen(false); }} className="rounded-full px-4 py-2 font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#A8998C] hover:text-[#F2E8DF] transition-colors">Cancel</button>
          <button data-testid={`moon-note-${i}-save`} disabled={busy || text.trim() === (e.note ?? "")} onClick={save} className="shrink-0 whitespace-nowrap inline-flex items-center gap-2 rounded-full bg-[#D9B47A] text-[#0A0706] px-4 py-2 font-tt-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors disabled:opacity-50">{busy && <Loader2 className="w-3 h-3 animate-spin" />} Keep note</button>
        </div>
      </div>
    </motion.div>
  );
};

const Entry = ({ e, i, onNote }) => (
  <motion.li initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 8) * 0.06 }} data-testid={`intention-entry-${i}`} className="grid grid-cols-[auto_1fr] gap-4 py-5 border-b border-[#D9B47A]/10">
    <span className={`mt-1 w-4 h-4 rounded-full ${e.bloomed ? "bg-[#F2E8DF] shadow-[0_0_14px_rgba(242,232,223,0.4)]" : "border border-[#D9B47A]/60 bg-[#120C0A]"}`} />
    <div className="min-w-0">
      <p className="font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#A8998C]">New moon · {fmt(e.cycle)}</p>
      <p data-testid={`intention-entry-${i}-text`} className="mt-2 font-display italic text-xl sm:text-2xl leading-snug break-words">“{e.text}”</p>
      <p className="mt-2 text-[13px] text-[#A8998C]">{e.bloomed ? `Returned to at the full moon · ${fmt(e.full_moon)}` : `Growing toward the full moon · ${fmt(e.full_moon)}`}</p>
      <MoonNote e={e} i={i} onSaved={onNote} />
    </div>
  </motion.li>
);

const CARD_THEMES = [["gold", "Candlelit gold", "#D9B47A", "#0A0706"], ["rose", "Rose", "#E8838E", "#12070A"], ["silver", "Silver moonlight", "#C9D3E0", "#070A10"]];

const prefsKey = (uid) => `tt-share-card-${uid ?? "me"}`;
const readPrefs = (uid) => {
  try {
    const p = JSON.parse(localStorage.getItem(prefsKey(uid)) ?? "{}");
    return {
      format: p?.format === "story" ? "story" : "square",
      theme: CARD_THEMES.some(([t]) => t === p?.theme) ? p.theme : "gold",
    };
  } catch {
    return { format: "square", theme: "gold" };
  }
};

const SharePanel = ({ year, name }) => {
  const { user } = useAuth();
  const [format, setFormat] = useState(() => readPrefs(user?.id).format);
  const [theme, setTheme] = useState(() => readPrefs(user?.id).theme);
  useEffect(() => {
    try {
      localStorage.setItem(prefsKey(user?.id), JSON.stringify({ format, theme }));
    } catch {
      // storage unavailable (private mode); choices simply aren't remembered
    }
  }, [user?.id, format, theme]);
  const [withQuote, setWithQuote] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [ready, setReady] = useState(false);
  const canvas = useRef(null);
  const quote = withQuote ? year[year.length - 1]?.text ?? "" : "";
  useEffect(() => {
    let alive = true;
    setReady(false);
    import("@/lib/keepsake")
      .then(({ drawShareCard }) => canvas.current && drawShareCard(canvas.current, { name, items: year, quote, format, theme }))
      .then(() => alive && setReady(true))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [year, name, quote, format, theme]);
  const share = async () => {
    setSharing(true);
    try {
      const { shareCard } = await import("@/lib/keepsake");
      const r = await shareCard({ name, items: year, quote, format, theme });
      if (r === "downloaded") toast.success(format === "story" ? "Your story card is downloading — ready for Instagram Stories." : "Your square card is downloading — ready for Instagram.");
    } catch {
      toast.error("The card couldn’t be made. Please try again.");
    } finally {
      setSharing(false);
    }
  };
  return (
    <div className="mt-5 pt-5 border-t border-[#D9B47A]/15 grid sm:grid-cols-[auto_1fr] gap-6 items-start">
      <div className="flex justify-center sm:block">
        <div className={`relative overflow-hidden border border-[#D9B47A]/25 bg-[#0A0706] shadow-[0_20px_50px_-20px_rgba(0,0,0,0.9)] transition-[width,height] duration-500 ${format === "story" ? "w-[135px] h-[240px]" : "w-[200px] h-[200px]"}`}>
          <canvas data-testid="share-card-preview" data-format={format} ref={canvas} aria-label={`Preview of your ${format} card`} className={`w-full h-full transition-opacity duration-300 ${ready ? "opacity-100" : "opacity-0"}`} />
          {!ready && <Loader2 className="absolute inset-0 m-auto w-5 h-5 animate-spin text-[#D9B47A]" />}
        </div>
      </div>
      <div>
        <p className="font-display text-lg">Share a card</p>
        <p className="mt-1 text-sm text-[#A8998C]">Made for Instagram: your moons as glowing orbs. Your intentions and notes stay private unless you choose to add one.</p>
        <div role="radiogroup" aria-label="Card format" className="mt-4 inline-flex rounded-full border border-[#D9B47A]/30 p-1">
          {[["square", "Square post", Share2], ["story", "Story", Smartphone]].map(([f, l, Icon]) => (
            <button key={f} role="radio" aria-checked={format === f} data-testid={`share-format-${f}`} onClick={() => setFormat(f)} className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-3 sm:px-4 py-2 font-tt-mono text-[11px] uppercase tracking-[0.15em] sm:tracking-[0.2em] transition-colors ${format === f ? "bg-[#D9B47A] text-[#0A0706]" : "text-[#CFC2B6] hover:text-[#D9B47A]"}`}>
              <Icon className="w-3.5 h-3.5" /> {l}
            </button>
          ))}
        </div>
        <div role="radiogroup" aria-label="Card colour" className="mt-4 flex flex-wrap items-center gap-3">
          {CARD_THEMES.map(([t, l, accent, bg]) => (
            <button key={t} role="radio" aria-checked={theme === t} aria-label={l} title={l} data-testid={`share-theme-${t}`} onClick={() => setTheme(t)} className={`group inline-flex items-center gap-2 rounded-full border py-1.5 pl-1.5 pr-3 text-[13px] transition-colors ${theme === t ? "border-[#F2E8DF]/70 text-[#F2E8DF]" : "border-[#D9B47A]/20 text-[#A8998C] hover:border-[#D9B47A]/50"}`}>
              <span className="w-6 h-6 rounded-full border" style={{ background: `radial-gradient(circle at 35% 35%, ${accent} 0 38%, ${bg} 42%)`, borderColor: accent }} />
              {l}
            </button>
          ))}
        </div>
        <label className="mt-4 flex items-center gap-2 text-sm text-[#CFC2B6] cursor-pointer">
          <input data-testid="share-card-quote" type="checkbox" checked={withQuote} onChange={(ev) => setWithQuote(ev.target.checked)} className="accent-[#C2414F] w-4 h-4" />
          Include my latest intention
        </label>
        <button data-testid="share-card-button" disabled={sharing || !ready} onClick={share} className="mt-5 inline-flex items-center justify-center gap-2 rounded-full border border-[#D9B47A]/50 text-[#D9B47A] px-5 py-3 font-tt-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors disabled:opacity-60">
          {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Share {format === "story" ? "story" : "post"}
        </button>
      </div>
    </div>
  );
};

const KeepsakeButton = ({ items, name }) => {
  const [busy, setBusy] = useState(false);
  const year = useMemo(() => items.filter((e) => Date.now() - new Date(e.cycle) < 366 * 86400000).slice().reverse(), [items]);
  if (!year.length) return null;
  const go = async () => {
    setBusy(true);
    try {
      const { downloadKeepsake } = await import("@/lib/keepsake");
      await downloadKeepsake({ name: name || "an initiate", items: year });
      toast.success("Your year of moons is downloading.");
    } catch {
      toast.error("The keepsake couldn’t be made. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-6 border border-[#D9B47A]/20 p-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="font-display text-xl">A year of moons</p>
          <p className="mt-1 text-sm text-[#A8998C]">A keepsake image of your last {year.length === 1 ? "intention" : `${year.length} intentions`} and moon notes, moon by moon.</p>
        </div>
        <button data-testid="keepsake-download" disabled={busy} onClick={go} className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-[#D9B47A] text-[#0A0706] px-5 py-3 font-tt-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download keepsake
        </button>
      </div>
      <SharePanel year={year} name={name} />
    </div>
  );
};

export default function IntentionJournal() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const ref = useRef(null);
  const load = useCallback(() => {
    withRefresh(() => api.get("/intentions"))
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]));
  }, []);
  const onNote = useCallback((cycle, note) => setItems((xs) => xs.map((x) => (x.cycle === cycle ? { ...x, note } : x))), []);
  useEffect(() => {
    if (!user?.initiate) return undefined;
    load();
    if (window.location.hash !== "#intentions") return undefined;
    const t = setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 600);
    return () => clearTimeout(t);
  }, [user?.initiate, load]);
  if (!user?.initiate || !process.env.REACT_APP_BACKEND_URL) return null;
  return (
    <section ref={ref} id="intentions" data-testid="intention-journal" className="scroll-mt-28 border border-[#D9B47A]/20 bg-[#0D0908] p-7 sm:p-9">
      <div className="flex items-center gap-3">
        <Moon className="w-5 h-5 text-[#D9B47A]" />
        <p className="font-tt-mono text-[12px] uppercase tracking-[0.3em] text-[#D9B47A]">Intention journal</p>
      </div>
      <p className="font-display text-2xl sm:text-3xl mt-5 leading-snug">Every seed you have planted, <em className="text-[#D9B47A]">moon by moon.</em></p>
      <MoonCalendar />
      <IntentionKeeper tid="journal-intention" label="This cycle’s intention" onSaved={load} />
      {items.length ? (
        <ol data-testid="intention-list" className="mt-6">{items.map((e, i) => <Entry key={e.cycle} e={e} i={i} onNote={onNote} />)}</ol>
      ) : (
        <p data-testid="intention-empty" className="mt-6 text-sm text-[#A8998C]">Nothing planted yet. At the next new moon, write one intention above and it will be kept here.</p>
      )}
      <MoonSwitch />
      <KeepsakeButton items={items} name={user?.name} />
    </section>
  );
}
